// Lightweight, dependency-free wishlist: entirely localStorage-based, no
// server calls, no impact on page weight beyond this one small file. Loaded
// once site-wide (see layout/theme.liquid) so every heart button and the
// header count badge share the exact same state.

const STORAGE_KEY = 'pk_wishlist';
const CHANGE_EVENT = 'wishlist:change';

/** @returns {string[]} */
function readList() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Storage unavailable (private browsing quota, disabled storage, etc.) -
    // the feature just quietly does nothing rather than throwing.
    return [];
  }
}

/** @param {string[]} handles */
function writeList(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    return true;
  } catch {
    return false;
  }
}

/** @returns {string[]} */
export function getWishlist() {
  return readList();
}

/** @param {string} handle */
export function isWishlisted(handle) {
  if (!handle) return false;
  return readList().includes(handle);
}

/**
 * @param {string} handle
 * @returns {boolean} whether the product is now in the list
 */
export function toggleWishlist(handle) {
  if (!handle) return false;
  const list = readList();
  const index = list.indexOf(handle);
  let inList;
  if (index === -1) {
    list.push(handle);
    inList = true;
  } else {
    list.splice(index, 1);
    inList = false;
  }
  writeList(list);
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handle, inList, count: list.length } }));
  return inList;
}

/** @param {string} handle */
export function removeFromWishlist(handle) {
  if (!handle) return;
  const list = readList();
  const index = list.indexOf(handle);
  if (index === -1) return;
  list.splice(index, 1);
  writeList(list);
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handle, inList: false, count: list.length } }));
}

/** @returns {number} */
export function wishlistCount() {
  return readList().length;
}

export const WISHLIST_CHANGE_EVENT = CHANGE_EVENT;

/**
 * <wishlist-button>: a self-contained heart toggle. Works from just its own
 * data-product-handle attribute - no page-specific wiring needed. Stays in
 * sync with every other instance of the same product on the page (e.g. the
 * same item shown again in a "related products" row) via the shared change
 * event, not just its own click.
 */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;
    this.button = this.querySelector('button');
    this.label = this.button?.querySelector('.wishlist-button__label');
    if (!this.button || !this.handle) return;

    this.#render();

    this.button.addEventListener('click', this.#handleClick);
    document.addEventListener(WISHLIST_CHANGE_EVENT, this.#handleExternalChange);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
    document.removeEventListener(WISHLIST_CHANGE_EVENT, this.#handleExternalChange);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    // Stops the click from reaching a parent link (the product card's own
    // full-card overlay <a>, in particular) - tapping the heart must never
    // also navigate to the product.
    event.preventDefault();
    event.stopPropagation();
    toggleWishlist(this.handle);
  };

  /** @param {CustomEvent} event */
  #handleExternalChange = (event) => {
    if (event.detail?.handle !== this.handle) return;
    this.#render();
  };

  #render() {
    const active = isWishlisted(this.handle);
    const T = window.pkT || {};
    const label = active ? T.removeFavorite || 'Odebrat z oblíbených' : T.addFavorite || 'Přidat do oblíbených';
    this.button.setAttribute('aria-pressed', String(active));
    this.button.setAttribute('aria-label', label);
    // The card-overlay variant keeps this span hidden via CSS (icon only,
    // no room for text there) - the inline/product-page variant shows it.
    // Updated either way so it's correct the moment CSS reveals it, with no
    // separate code path per variant.
    if (this.label) this.label.textContent = label;
    this.classList.toggle('wishlist-button--active', active);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * <wishlist-count>: small badge in the header showing how many items are
 * saved. Reads the count once on connect and stays in sync via the same
 * change event - no polling, no fetch, no page reload needed anywhere on
 * the site for the number to stay correct.
 */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.#render();
    document.addEventListener(WISHLIST_CHANGE_EVENT, this.#render);
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_CHANGE_EVENT, this.#render);
  }

  #render = () => {
    const count = wishlistCount();
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  };
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
